import { Link } from "react-router-dom";
import { Heart, ArrowRight, Sparkles } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import DonateDialog from "@/components/DonateDialog";
import { IMG, PASTOR_BIO, COVERING } from "@/lib/content";

const VALUES = [
  { title: "Compassion First", desc: "We lead with love, meeting people exactly where they are." },
  { title: "Faith in Action", desc: "Belief becomes tangible — keys handed over, families fed, homes restored." },
  { title: "Dignity Always", desc: "Every person we serve is treated as worthy, seen, and valued." },
  { title: "Next Generation", desc: "We pour into young women and youth so hope carries forward." },
];

const LIFE = [
  { img: IMG.lifeAltar, tag: "At the Altar", title: "Ministering under the promise of Jude 24-25", top: true, alt: "Pastor Jewel Jones ministering at the altar" },
  { img: IMG.lifeBaptism, tag: "New Life", title: "Buried with Him, raised to new life", top: true, alt: "A believer being baptized during worship at Greater Faith" },
  { img: IMG.lifeSanctuary, tag: "Gather", title: "Our sanctuary, ready for worship", alt: "Greater Faith sanctuary set for service with Lion of Judah banner" },
  { img: IMG.lifePraiseDance, tag: "Praise", title: "Our young praise dancers", top: true, alt: "Four young praise dancers in purple and gold" },
  { img: IMG.lifeMusic, tag: "Worship", title: "Music ministry in motion", alt: "Musicians preparing to lead worship at Greater Faith" },
  { img: IMG.lifeServe, tag: "Serve", title: "Hands that serve, young and old", top: true, alt: "A young member and an adult serving and cleaning the ministry" },
  { img: IMG.youthChoir, tag: "Youth Choir", title: "Raising the next generation of praise", alt: "Greater Faith youth choir in black and gold robes" },
  { img: IMG.churchExterior, tag: "Our Home", title: "Our doors on E. Virginia Beach Blvd", alt: "Greater Faith Ministries building" },
  { img: IMG.husbandNavy, tag: "Faith & Service", title: "Elder Algie Jones, U.S. Navy", top: true, alt: "Elder Algie Jones in U.S. Navy uniform with Pastor Jewel Jones" },
];

export default function About() {
  return (
    <>
      <PageHero
        testid="about-hero"
        kicker="About Us"
        title="Rooted in faith. Built for the community."
        subtitle="Greater Faith International Ministries is a place where worship and works meet — and where lives are changed one relationship at a time."
        image={IMG.leaders}
      />

      {/* PASTOR BIO */}
      <section className="py-24 sm:py-32 relative z-10" data-testid="pastor-bio">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-14 items-start">
          <Reveal className="lg:col-span-2 lg:sticky lg:top-28">
            <div className="img-zoom rounded-2xl shadow-2xl relative">
              <img src={IMG.leaderCert} alt="Pastor and Founder Jewel Jones and Elder Algie Jones" className="w-full h-[520px] object-cover object-top rounded-2xl" />
            </div>
            <div className="mt-6 bg-plum rounded-2xl p-6 text-cream">
              <Sparkles className="text-gold mb-2" size={22} />
              <p className="font-heading text-2xl">Pastor and Founder Jewel Jones</p>
              <p className="text-cream/70 text-sm mt-1">with Elder Algie Jones</p>
            </div>
          </Reveal>

          <div className="lg:col-span-3">
            <SectionHeading kicker="Our Leader" title="A pastor devoted to loving God and loving children" />
            <div className="mt-8 space-y-6">
              {PASTOR_BIO.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <p className={`text-stone leading-relaxed ${i === 0 ? "text-lg text-ink first-letter:font-heading first-letter:text-6xl first-letter:text-plum first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8] first-letter:mt-1" : "text-base"}`}>
                    {p}
                  </p>
                </Reveal>
              ))}
            </div>
            <Reveal>
              <DonateDialog trigger={
                <button className="mt-9 bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-7 py-3.5 flex items-center gap-2 transition-all hover:-translate-y-1" data-testid="about-donate-btn">
                  <Heart size={17} /> Support the Ministry
                </button>
              } />
            </Reveal>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 sm:py-28 bg-white relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading kicker="What Guides Us" title="Our values in every act of service" align="center" />
          <Stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <StaggerItem key={v.title}>
                <div className="h-full bg-cream rounded-2xl p-7 border border-plum/10 lift">
                  <span className="font-heading text-5xl text-gold/40 font-semibold">0{i + 1}</span>
                  <h3 className="font-heading text-2xl text-plum font-semibold mt-3">{v.title}</h3>
                  <p className="text-stone text-sm mt-2 leading-relaxed">{v.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* MINISTRY LIFE */}
      <section className="py-20 sm:py-28 relative z-10" data-testid="ministry-life">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading kicker="Life at Greater Faith" title="Come as you are. Leave forever changed." subtitle="This is where the weary find rest, the broken find healing, and strangers become family. From the water of baptism to a child's first praise dance, from a hand on a mop to a hand raised in worship — every moment here is a life being loved back to wholeness." />
          <Stagger className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {LIFE.map((it, i) => (
              <StaggerItem key={i} className="img-zoom rounded-2xl shadow-xl relative overflow-hidden h-56 sm:h-72 lg:h-80">
                <img src={it.img} alt={it.alt} className={`w-full h-full rounded-2xl object-cover ${it.top ? "object-top" : "object-center"}`} data-testid={`life-photo-${i}`} />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-plum-dark/90 via-plum-dark/30 to-transparent p-4 sm:p-5 rounded-b-2xl">
                  <p className="text-gold-light text-xs tracking-[0.18em] uppercase font-semibold">{it.tag}</p>
                  <p className="text-cream font-heading text-lg sm:text-xl leading-snug">{it.title}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>


      {/* COVERING */}
      <section className="py-24 sm:py-32 relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="bg-plum-dark rounded-3xl overflow-hidden grid lg:grid-cols-5">
            <div className="lg:col-span-2 img-zoom">
              <img src={IMG.overseers} alt="Apostle Dr. Danny Ducksworth and Overseer Rebecca Ducksworth" className="w-full h-72 lg:h-full object-contain bg-plum-dark" />
            </div>
            <div className="lg:col-span-3 p-10 sm:p-14 text-cream">
              <span className="text-gold-light text-sm tracking-[0.22em] uppercase font-semibold">Spiritual Covering</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold mt-4">{COVERING.title}</h2>
              <p className="text-cream/80 text-lg mt-6 leading-relaxed">{COVERING.body}</p>
              <p className="text-gold-light font-heading text-2xl mt-6">Apostle Dr. Danny Ducksworth &amp; Overseer Rebecca Ducksworth</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 sm:pb-32 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="rounded-3xl overflow-hidden relative">
            <img src={IMG.pray} alt="Prayer and service" className="w-full h-72 object-cover" />
            <div className="absolute inset-0 bg-plum/80 flex flex-col items-center justify-center text-center px-6">
              <h3 className="font-heading text-3xl sm:text-4xl text-cream font-semibold">Come be part of the story</h3>
              <div className="flex flex-wrap gap-4 justify-center mt-6">
                <Link to="/get-involved" className="bg-gold hover:bg-gold-dark text-plum font-semibold rounded-full px-7 py-3.5 flex items-center gap-2 transition-all hover:-translate-y-1" data-testid="about-cta-involved">Get Involved <ArrowRight size={17} /></Link>
                <Link to="/contact" className="border border-cream/40 text-cream rounded-full px-7 py-3.5 hover:bg-cream/10 transition-colors" data-testid="about-cta-contact">Visit Us</Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
